<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import CeaDynamicCatalogsAPI from 'dashboard/api/ceaDynamicCatalogs';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const props = defineProps({
  catalog: { type: Object, required: true },
  entry: { type: Object, default: null },
});

const emit = defineEmits(['saved', 'cancel']);

const { t } = useI18n();

const form = ref({
  code: '',
  description: '',
  active: true,
  custom_values: {},
});

const saving = ref(false);
const isEditing = computed(() => !!props.entry?.id);

const schemaFields = computed(() => props.catalog?.fields_schema || []);

const modalTitle = computed(() =>
  isEditing.value
    ? t('CEA_LOOKUPS.FORM.EDIT_TITLE', { type: props.catalog.name })
    : t('CEA_LOOKUPS.FORM.NEW_TITLE', { type: props.catalog.name })
);

const initializeForm = () => {
  const customValues = {};
  schemaFields.value.forEach(field => {
    const existing = props.entry?.custom_values?.[field.key];
    if (field.field_type === 'boolean') {
      customValues[field.key] = existing === true;
    } else {
      customValues[field.key] = existing ?? '';
    }
  });

  form.value = {
    code: props.entry?.code || '',
    description: props.entry?.description || '',
    active: props.entry?.active !== false,
    custom_values: customValues,
  };
};

onMounted(initializeForm);
watch(() => props.entry, initializeForm);

const buildPayload = () => {
  const customValues = { ...form.value.custom_values };

  schemaFields.value.forEach(field => {
    if (field.field_type === 'number') {
      const raw = customValues[field.key];
      customValues[field.key] =
        raw === '' || raw === null || raw === undefined ? null : Number(raw);
    }
    if (field.field_type === 'boolean') {
      customValues[field.key] = !!customValues[field.key];
    }
  });

  return {
    code: form.value.code.trim(),
    description: form.value.description.trim(),
    active: form.value.active,
    custom_values: customValues,
  };
};

const handleSubmit = async () => {
  saving.value = true;

  try {
    const payload = buildPayload();
    if (isEditing.value) {
      await CeaDynamicCatalogsAPI.updateEntry(
        props.catalog.id,
        props.entry.id,
        payload
      );
      useAlert(t('CEA_LOOKUPS.UPDATE_SUCCESS'));
    } else {
      await CeaDynamicCatalogsAPI.createEntry(props.catalog.id, payload);
      useAlert(t('CEA_LOOKUPS.CREATE_SUCCESS'));
    }
    emit('saved');
  } catch (error) {
    const errors = error.response?.data?.errors || [
      t('CEA_LOOKUPS.SAVE_ERROR'),
    ];
    useAlert(Array.isArray(errors) ? errors.join(', ') : errors);
  } finally {
    saving.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col w-full max-w-lg gap-4 p-6">
    <h2 class="text-lg font-medium text-n-slate-12">
      {{ modalTitle }}
    </h2>

    <div class="flex flex-col gap-3">
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('CEA_LOOKUPS.FORM.CODE') }} *
        </label>
        <Input
          v-model="form.code"
          :placeholder="$t('CEA_LOOKUPS.FORM.CODE_PLACEHOLDER')"
        />
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('CEA_LOOKUPS.FORM.DESCRIPTION') }} *
        </label>
        <Input
          v-model="form.description"
          :placeholder="$t('CEA_LOOKUPS.FORM.DESCRIPTION')"
        />
      </div>

      <div
        v-for="field in schemaFields"
        :key="field.key"
        class="flex flex-col gap-1"
      >
        <label class="text-sm font-medium text-n-slate-12">
          {{ field.label }}
          <span v-if="field.required">*</span>
        </label>

        <template v-if="field.field_type === 'boolean'">
          <div class="flex items-center gap-2">
            <input
              :id="`custom-${field.key}`"
              v-model="form.custom_values[field.key]"
              type="checkbox"
              class="rounded border-n-weak"
            />
            <label :for="`custom-${field.key}`" class="text-sm text-n-slate-11">
              {{ field.label }}
            </label>
          </div>
        </template>

        <template v-else-if="field.field_type === 'select'">
          <select
            v-model="form.custom_values[field.key]"
            class="w-full px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-solid-2 text-n-slate-12"
          >
            <option value="">
              {{ $t('CEA_LOOKUPS.CATALOG_WIZARD.SELECT_PLACEHOLDER') }}
            </option>
            <option
              v-for="option in field.options || []"
              :key="option"
              :value="option"
            >
              {{ option }}
            </option>
          </select>
        </template>

        <template v-else>
          <Input
            v-model="form.custom_values[field.key]"
            :type="field.field_type === 'number' ? 'number' : 'text'"
            :placeholder="field.label"
          />
        </template>
      </div>

      <div class="flex items-center gap-2">
        <input
          id="entry-active"
          v-model="form.active"
          type="checkbox"
          class="rounded border-n-weak"
        />
        <label for="entry-active" class="text-sm text-n-slate-12">
          {{ $t('CEA_LOOKUPS.FORM.ACTIVE') }}
        </label>
      </div>
    </div>

    <div class="flex items-center justify-end gap-2 pt-2 border-t border-n-weak">
      <Button
        faded
        slate
        :label="$t('CEA_LOOKUPS.FORM.CANCEL')"
        @click="emit('cancel')"
      />
      <Button
        :label="isEditing ? $t('CEA_LOOKUPS.FORM.UPDATE') : $t('CEA_LOOKUPS.FORM.CREATE')"
        :is-loading="saving"
        @click="handleSubmit"
      />
    </div>
  </div>
</template>
